/**
 * Streak tracking — web mirror of services/streak.ts.
 * Streak lives on the ksnap_users/{uid} doc as { current, best, lastLogDate }.
 */
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { COLLECTIONS } from '@/lib/collections'
import { updateUserProfile } from '@/lib/firestore/profile'
import { addDays, todayStr } from '@/lib/dates'
import { track } from '@/lib/analytics'

export interface StreakData {
  current: number
  best: number
  lastLogDate: string // 'YYYY-MM-DD'
}

export const STREAK_MILESTONES = [
  { days: 3, emoji: '🔥', titleKey: 'streak.milestones.3days' },
  { days: 7, emoji: '⭐', titleKey: 'streak.milestones.7days' },
  { days: 14, emoji: '🌟', titleKey: 'streak.milestones.14days' },
  { days: 30, emoji: '🏆', titleKey: 'streak.milestones.30days' },
  { days: 60, emoji: '💎', titleKey: 'streak.milestones.60days' },
  { days: 100, emoji: '👑', titleKey: 'streak.milestones.100days' },
] as const

const DEFAULT_STREAK: StreakData = { current: 0, best: 0, lastLogDate: '' }

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Pure display helper: the streak count that should be SHOWN right now.
 * Stored `current` only resets lazily; if lastLogDate is older than yesterday
 * (local dates) the streak is broken → 0.
 */
export function getEffectiveStreak(
  streak: StreakData | null | undefined,
): number {
  if (!streak || !DATE_RE.test(streak.lastLogDate)) return 0
  if (streak.lastLogDate < addDays(todayStr(), -1)) return 0
  return streak.current || 0
}

/**
 * Update the streak after a diary entry is created — same rules as mobile:
 * today = no change; yesterday = increment; older/invalid = reset to 1.
 * Fire-and-forget from callers; idempotent for same-day calls.
 */
export async function updateStreak(userId: string): Promise<void> {
  const today = todayStr()
  const yesterday = addDays(today, -1)

  const snap = await getDoc(doc(db, COLLECTIONS.USERS, userId))
  const streak: StreakData = (snap.data()?.streak as StreakData) || { ...DEFAULT_STREAK }

  if (streak.lastLogDate === today) return
  // Future-date edge case — no change.
  if (DATE_RE.test(streak.lastLogDate) && streak.lastLogDate > today) return

  let updated: StreakData
  if (streak.lastLogDate === yesterday) {
    const newCurrent = streak.current + 1
    updated = { current: newCurrent, best: Math.max(newCurrent, streak.best), lastLogDate: today }
  } else {
    updated = { current: 1, best: Math.max(1, streak.best), lastLogDate: today }
  }

  await updateUserProfile(userId, { streak: updated })

  if (STREAK_MILESTONES.some((m) => m.days === updated.current)) {
    track('streak_milestone', { days: updated.current })
  }
}

export function getNextMilestone(currentStreak: number) {
  return STREAK_MILESTONES.find((m) => m.days > currentStreak) || null
}
